/** @jsx jsx */
import { jsx } from '@emotion/react';

import type { DescriptionPage_DifficultyLevel as DifficultyLevel } from '__generated__/graphql-types';
import type { CmlContent } from '__generated__/graphql-types-do-not-use';

import { ContextualHelp, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import KeyInformationDetailsItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationDetailsItem';
import RecommendedExperienceModal from 'bundles/unified-description-page-common/components/modules/key-information/RecommendedExperienceModal';
import { getDefaultDifficultyDetail, humanizeDifficultyLevel } from 'bundles/unified-description-page-common/constants';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

import _t from 'i18n!nls/xdp';

type Props = {
  difficultyLevel?: DifficultyLevel | null;
  cmlRecommendedBackground?: CmlContent | null;
  numSubtitleLines?: number;
};

const KeyInformationDifficultyItem = ({ difficultyLevel, cmlRecommendedBackground, numSubtitleLines }: Props) => {
  const track = useTracker();
  const eventingProductData = useEventingProductData();

  if (!difficultyLevel) return null;

  const onExpandedStateChange = () => {
    track('click_button', {
      button: { name: 'description_page_recommended_experience' },
      pageSection: {
        sectionName: 'kim',
      },
      ...eventingProductData,
    });
  };

  const difficultyLevelTitle = _t('#{difficulty} level', {
    difficulty: humanizeDifficultyLevel(difficultyLevel),
  });

  return (
    <KeyInformationDetailsItem
      title={difficultyLevelTitle}
      subtitle={
        cmlRecommendedBackground ? (
          <TrackedDiv trackingName="recommended_experience_help">
            <ContextualHelp
              helpToggleProps={{
                'aria-label': _t('Information about difficulty level pre-requisites.'),
              }}
              tooltipProps={{
                closeButtonProps: {
                  'aria-label': _t('Close modal about difficulty level pre-requisites.'),
                },
              }}
              label={
                <Typography2 component="p" color="supportText" variant="bodySecondary">
                  {_t('Recommended experience')}
                </Typography2>
              }
              onChange={onExpandedStateChange}
            >
              {({ onClose, isExpanded }) => {
                return (
                  <RecommendedExperienceModal
                    setOpen={onClose}
                    open={!!isExpanded}
                    difficulty={difficultyLevel}
                    cmlRecommendedBackground={cmlRecommendedBackground}
                  />
                );
              }}
            </ContextualHelp>
          </TrackedDiv>
        ) : (
          getDefaultDifficultyDetail()[difficultyLevel]
        )
      }
      numSubtitleLines={numSubtitleLines}
    />
  );
};

export default KeyInformationDifficultyItem;
